import { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate, Link,useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import lightLogo from "../assets/light.png"; // Ensure you have a light.png file in the assets folder
import darkLogo from "../assets/dark.png"; // Ensure you have a dark.png file in the assets folder
import { OverlayTrigger, Popover, Modal } from "react-bootstrap"; // Import OverlayTrigger and Popover from react-bootstrap
import BellIcon from "../assets/BellIcon"; // Import BellIcon
import ReplyModal from "./ReplyModal"; // Import ReplyModal
import ConfirmationPopup from "./common/ConfirmationPopup"; // Import ConfirmationPopup
import ModelSearch from "./common/ModelSearch"; // Import ModelSearch
import NotesModal from "./NotesModal";
import ReminderModal from "./ReminderModal"; // Import ReminderModal
import ScheduledTasksModal from "./ScheduledTasksModal"; // Import ScheduledTasksModal
import useResetSession from "./common/ResetSession";
import ThemeSwitchLoader from "./common/ThemeSwitchLoader"; // Import ThemeSwitchLoader
import UserGuideModal from "./UserGuideModal";

const initializeTheme = () => {
  const savedTheme = Cookies.get("isDarkMode");
  if (savedTheme !== undefined) {
    return savedTheme === "true";
  }
  return (
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
};

const ReplyIcon = ({ size = 20, color = "#1A73E8" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 9V5L3 12L10 19V15C15 15 18.5 16.5 21 20C20 15 17 10 10 9Z"
      fill={color}
    />
  </svg>
);

const ViewIcon = ({ size = 20, color = "#1A73E8" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 4.5C7.30558 4.5 3.27273 7.61111 1.5 12C3.27273 16.3889 7.30558 19.5 12 19.5C16.6944 19.5 20.7273 16.3889 22.5 12C20.7273 7.61111 16.6944 4.5 12 4.5ZM12 17.5C8.96243 17.5 6.5 15.0376 6.5 12C6.5 8.96243 8.96243 6.5 12 6.5C15.0376 6.5 17.5 8.96243 17.5 12C17.5 15.0376 15.0376 17.5 12 17.5ZM12 9C10.6193 9 9.5 10.1193 9.5 11.5C9.5 12.8807 10.6193 14 12 14C13.3807 14 14.5 12.8807 14.5 11.5C14.5 10.1193 13.3807 9 12 9Z"
      fill={color}
    />
  </svg>
);

function Navbar({
  onClearChat,
  currentRoute,
  toggleTheme,
  isDarkMode,
  notifications = [],
  setNotifications,
  isSidebarVisible,
  onOpenReminder,
  onOpenMeeting,
  onOpenSettings,
  onOpenGrocery,
  onOpenQueue,
  onOpenVoiceHistory,
  onOpenScratchpad,
  onOpenNotification,
  onOpenScheduledTasks,
  userDisplayName,
}) {
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState(null);
  const [userName, setUserName] = useState("");
  const [unreadNotifications, setUnreadNotifications] = useState(
    notifications.length
  );
  const [showPopover, setShowPopover] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [workflowUrl, setWorkflowUrl] = useState(""); // Add state for workflow URL
  const navigate = useNavigate();
  const location = useLocation();
  const [showNotesModal, setShowNotesModal] = useState(false); // Add this state
  const [showReminderModal, setShowReminderModal] = useState(false); // Add state for reminder modal
  const [showScheduledTasksModal, setShowScheduledTasksModal] = useState(false); // Add state for scheduled tasks modal
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false); // Add state for confirmation popup
  const [showLogoutModal, setShowLogoutModal] = useState(false); // Add state for logout confirmation popup
  const [showModalSearch, setShowModalSearch] = useState(true);
  const [showThemeLoader, setShowThemeLoader] = useState(false); // Add state for theme switch loader
  const [showUserGuideModal, setShowUserGuideModal] = useState(false);
  const [pendingTheme, setPendingTheme] = useState(null); // Track the theme we're switching to
  const [sessionName, setSessionName] = useState("Main Topic");
  const [parentSessionName, setParentSessionName] = useState(null);
  const [parentSessionId, setParentSessionId] = useState(null);
  const [sessionType, setSessionType] = useState(null);
  const popoverRef = useRef(null);
  
  const resetSession = useResetSession();

  // Handle theme toggle with animation
  const handleThemeToggle = () => {
    const newTheme = !isDarkMode;
    setPendingTheme(newTheme);
    setShowThemeLoader(true);

    // After animation, actually toggle theme and hide loader
    setTimeout(() => {
      toggleTheme();
      setTimeout(() => {
        setShowThemeLoader(false);
        setPendingTheme(null);
      }, 400);
    }, 500);
  };

  useEffect(() => {
    if (onOpenReminder) {
      setShowReminderModal(true);
    }
  }, [onOpenReminder]);

  // Handle meeting open trigger from voice assistant
  useEffect(() => {
    if (onOpenMeeting?.trigger) {
      // Navigate to meetings page
      navigate('/meetings');
      // If specific meeting ID is provided, you can handle it here
      // For now, just navigate to meetings page
    }
  }, [onOpenMeeting?.trigger]);

  // Handle settings open trigger from voice assistant
  useEffect(() => {
    if (onOpenSettings) {
      // Navigate to settings/profile page
      navigate('/userprofile');
    }
  }, [onOpenSettings]);

  // Handle grocery open trigger from voice assistant
  useEffect(() => {
    if (onOpenGrocery) {
      // Navigate to grocery page
      navigate('/grocery');
    }
  }, [onOpenGrocery]);

  // Handle queue open trigger from voice assistant
  useEffect(() => {
    if (onOpenQueue) {
      // Navigate to queue page
      navigate('/queue');
    }
  }, [onOpenQueue]);

  // Handle voice history open trigger from voice assistant
  useEffect(() => {
    if (onOpenVoiceHistory) {
      // Navigate to voice history page
      navigate('/voice-history');
    }
  }, [onOpenVoiceHistory]);

  // Handle scratchpad open trigger from voice assistant
  useEffect(() => {
    if (onOpenScratchpad) {
      // Open the scratchpad modal
      setShowNotesModal(true);
    }
  }, [onOpenScratchpad]);

  // Handle notification open trigger from voice assistant
  useEffect(() => {
    if (onOpenNotification) {
      // Open the notifications popover/section
      setShowPopover(true);
    }
  }, [onOpenNotification]);

  // Handle scheduled tasks open trigger from voice assistant
  useEffect(() => {
    if (onOpenScheduledTasks) {
      // Open the scheduled tasks modal
      setShowScheduledTasksModal(true);
    }
  }, [onOpenScheduledTasks]);

  // Update userName when userDisplayName prop changes (e.g., after onboarding)
  useEffect(() => {
    if (userDisplayName) {
      setUserName(userDisplayName);
    }
  }, [userDisplayName]);

  useEffect(() => {
    async function fetchSession() {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      setUser(user);
      setUserId(user?.id);
      // console.log("User ID:", user);
      if (user) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("name, workflow_url")
          .eq("id", user.id)
          .single();
        setUserName(profile?.name || user.email);
        setWorkflowUrl(profile?.workflow_url); // Set workflow URL
      }
    }
    fetchSession();

    const { data: subscription } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user);
        if (session?.user) {
          const fetchProfile = async () => {
            const { data: profile } = await supabase
              .from("profiles")
              .select("name, workflow_url")
              .eq("id", session.user.id)
              .single();
            setUserName(profile?.name || session.user.email);
            setWorkflowUrl(profile?.workflow_url); // Set workflow URL
          };
          fetchProfile();
        }
      }
    );
    const chatSessionId = Cookies.get("chat_session_id");
    if (chatSessionId && chatSessionId !== "undefined") {
      const fetchChatSession = async () => {
        const { data, error } = await supabase
          .from("chat_session")
          .select("*")
          .eq("id", chatSessionId)
          .single();

        let parent = null;

        if (error) {
          console.error("Error fetching chat session:", error);

          // If session not found, clear the invalid cookie
          if (error.code === 'PGRST116') {
            Cookies.remove('chat_session_id');
            setSessionName("Main Topic");
            setSessionType(null);
          }
        } else {

          const parentId = data?.parent_session;
          if (parentId) {
            const { data: parentData, error: parentError } = await supabase
              .from("chat_session")
              .select("*")
              .eq("id", parentId)
              .single();
            
            if (parentError) {
              console.error("Error fetching parent session:", parentError);
            } else {
              parent = parentData;
            }
          }


          if (data.type === "chatbot") {
            setShowModalSearch(false);
          }
          if (data.type === "notification") {
            setSessionName(data.name);
            setSessionType("(Notification Topic)");
          } else if (data.type === "sub_topic") {
            setParentSessionName(parent?.name);
            setParentSessionId(parent?.id);
            setSessionName(data.name);
            setSessionType("(Subtopic)");
          } else if (data.type === "topic") {
            setSessionName(data.name);
            setSessionType("(Topic)");
          } else if (data.type === "chatbot") {
            setSessionName(data.name);
            setSessionType("(Custom Chatbot)");
          } else {
            setSessionName("Main Topic");
          }

          //  notification
          //  side
          //  main
          //  chatbot
        }
      };
      fetchChatSession();
    }
    return () => {
      subscription?.unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    setUnreadNotifications(notifications.length);
  }, [notifications]);

  useEffect(() => {
    if (user) {
      fetchNotifications();
    }
  }, [user]);

  const fetchNotifications = async () => {
    const { data, error } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", user.id)
      // .eq('is_summary', false)
      .eq("alert_popup", true)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching notifications:", error);
    } else {
      setNotifications(data);
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogout = async () => {
    // Dispatch logout event to cleanup components (e.g., MultimodalClient)
    window.dispatchEvent(new CustomEvent('zoomey-logout'));

    // Give components time to cleanup before signing out
    await new Promise(resolve => setTimeout(resolve, 100));

    const { error } = await supabase.auth.signOut();
    setShowLogoutModal(false);

    // Remove session cookie
    if (typeof Cookies !== 'undefined') {
      Cookies.remove('chat_session_id');
    }

    navigate("/login");
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  useEffect(() => {
    document.body.classList.toggle("dark-mode", isDarkMode);
  }, [isDarkMode]);

  const handlePopoverClick = () => {
    setUnreadNotifications(0);
    setShowPopover(!showPopover);
  };

  const handleClickOutside = (event) => {
    if (
      popoverRef.current &&
      !popoverRef.current.contains(event.target) &&
      !event.target.closest(".popover")
    ) {
      setShowPopover(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const removeNotification = async (id) => {
    const { error } = await supabase
      .from("notifications")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Error removing notification:", error);
    } else {
      setUnreadNotifications((prev) => prev - 1);
      setNotifications((prev) =>
        prev.filter((notification) => notification.id !== id)
      );
    }
  };

  const clearAllNotifications = async () => {
    const { error } = await supabase
      .from("notifications")
      .delete()
      .eq("user_id", user.id);

    if (error) {
      console.error("Error clearing all notifications:", error);
    } else {
      setUnreadNotifications(0);
      setNotifications([]);
    }
  };

  const handleSendReply = (notificationId) => {
    if (replyMessage.trim() === "") return; // Prevent sending if reply message is blank
    console.log("Reply message:", replyMessage);
    setShowModal(false);
    setReplyMessage("");
    removeNotification(notificationId); // Remove the notification
  };

  const handleReplyClick = async (notification) => {
    setSelectedNotification(notification);
    setShowPopover(false); // Hide the popover
    setReplyMessage(notification.user_query || ""); // Set reply message to previous user_query if it exists, otherwise reset
    setShowModal(true);

    // Update the status of all notifications to true
    const { error } = await supabase
      .from("notifications")
      .update({ status: true })
      .eq("user_id", user.id);

    if (error) {
      console.error("Error updating notification status:", error);
    } else {
      // Fetch updated notifications
      fetchNotifications();
    }
  };

  function truncateText(text, limit) {
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  }

  const handleMainSessionClick = async () => {
    Cookies.remove("chat_session_id"); // Remove the chat_session_id cookie
    // navigate('/chat-bot');
    window.location.reload(); // Navigate to the chat-bot route
  };

  const popover = (
    <Popover id="popover-basic" style={{ minWidth: "300px" }}>
      <Popover.Header
        as="h3"
        className="d-flex justify-content-between align-items-center"
      >
        <p style={{ color: isDarkMode ? "black" : "black", margin: 0 }}>
          Notifications
        </p>
        <button
          className="btn btn-sm btn-default"
          onClick={clearAllNotifications}
        >
          Clear All
        </button>
      </Popover.Header>
      <Popover.Body
        style={{ maxHeight: "65vh", overflowY: "auto", overflowX: "hidden" }}
      >
        {notifications.length > 0 ? (
          notifications.map((notification, index) => (
            <div
              key={index}
              className="border-bottom mt-1"
              style={{ width: "260px" }}
            >
              <div className="d-flex justify-content-between align-items-start">
                <div style={{ flex: 1 }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <strong>{truncateText(notification.title, 20)}</strong>
                    <div>
                      <button
                        className="btn btn-sm"
                        style={{ marginLeft: "10px" }}
                        onClick={() => handleReplyClick(notification)}
                      >
                        {notification.user_query &&
                        notification.query_response ? (
                          <ViewIcon size={20} color="#1A73E8" />
                        ) : (
                          <ReplyIcon size={20} color="#1A73E8" />
                        )}
                      </button>
                      <button
                        className="btn btn-sm"
                        onClick={() => removeNotification(notification.id)}
                      >
                        &times;
                      </button>
                    </div>
                  </div>
                  <p className="mb-1">
                    {truncateText(notification.message, 85)}
                  </p>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p>No new notifications</p>
        )}
      </Popover.Body>
    </Popover>
  );

  return (
    <nav
      className="navbar navbar-expand-lg navbar-light"
      style={{
        padding: "0 16px",
        borderBottom: "1px solid var(--border-color)",
        height: "81px",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center"
      }}
    >
      <div className="container-fluid" style={{ padding: 0, height: "100%" }}>
        {showModalSearch ? (
          <div className="d-flex align-items-center d-none">
            <ModelSearch />
          </div>
        ) : null}

        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
          aria-controls="navbarNav"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>
        {/* Dropdown for external navigation */}
        {/* <div className="dropdown me-3">
          <button
            className="btn btn-secondary dropdown-toggle"
            type="button"
            id="externalDropdown"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            style={{ background: "#18181B", color: "#fff", border: "none" }}
          >
            Zoomey&nbsp;&nbsp;
          </button>

          <ul className="dropdown-menu" aria-labelledby="externalDropdown">
            <li>
              <button
                className="dropdown-item"
                onClick={() => {
                  window.location.href = "https://zoomey-coach-ai.lovable.app";
                }}
              >
                Focus Coach
              </button>
            </li>
          </ul>
        </div> */}
        {!isSidebarVisible  ? (
          <div className="collapse navbar-collapse justify-content-start">
            <Link className="navbar-brand zoomey-logo-link" to="#" onClick={handleMainSessionClick} style={{ marginLeft: "20px" }}>
              <div className="zoomey-logo-wrapper">
                {/* Animated Logo Mark */}
                <div className={`zoomey-logo-mark ${isDarkMode ? 'dark' : 'light'}`}>
                  <div className="logo-glow"></div>
                  <div className="logo-inner">
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M6 7H22L8 21H22"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="z-path"
                      />
                      <circle cx="22" cy="7" r="2" fill="currentColor" className="z-dot"/>
                    </svg>
                  </div>
                </div>
                {/* Brand Text */}
                <div className="zoomey-brand-text">
                  <span className={`brand-name ${isDarkMode ? 'dark' : 'light'}`}>ZOOMEY</span>
                </div>
              </div>
            </Link>
          </div>
        ) : (
          ""
        )}
        
        <div
          className={`collapse navbar-collapse ${
            isSidebarVisible ? "justify-content-start" : "justify-content-center"
          }`}
        >
          <div
            className="d-flex flex-column align-items-center"
            style={{
              textAlign: "center",
              marginBottom: "0px",
              fontWeight: "var(--font-weight)",
              fontSize: "var(--font-size-larger)",
              color: "var(--text)",
              marginLeft: "15px",
            }}
          >
            <div className="d-flex align-items-center">
              {parentSessionId != null && (
                <span
                  role="button"
                  onClick={() => resetSession(parentSessionId)}
                  style={{ cursor: "pointer", display: "inline-block", marginRight: "8px", opacity: 0.6 }}
                >
                  {parentSessionName}{'\u00A0\u2192'}
                </span>
              )}

              <span
                className={parentSessionId != null ? "active-subtopic-name" : ""}
                style={{ marginRight: "8px" }}
              >
                {sessionName == null ? "\u00A0" : sessionName}
              </span>
            </div>

            {sessionType == null ? null : (
              <div style={{ marginTop: "2px" }}>
                <b
                  className={parentSessionId != null ? "active-subtopic-badge" : ""}
                  style={{ fontWeight: "400", fontSize: "12px" }}
                >
                  {sessionType == null ? "\u00A0" : sessionType}
                </b>
              </div>
            )}
          </div>
        </div>

        <div className="collapse navbar-collapse justify-content-end">
          &nbsp;&nbsp;
          <button
            onClick={handleThemeToggle}
            style={{
              background: "transparent",
              border: "none",
              color: isDarkMode ? "white" : "#1A73E8",
              padding: "8.5px 12px",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => (e.target.style.opacity = 0.8)}
            onMouseLeave={(e) => (e.target.style.opacity = 1)}
          >
            {isDarkMode ? (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="var(--teal-bg)">
                  <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9c0-.46-.04-.92-.1-1.36-.98 1.37-2.58 2.26-4.4 2.26-3.03 0-5.5-2.47-5.5-5.5 0-1.82.89-3.42 2.26-4.4-.44-.06-.9-.1-1.36-.1z" />
                </svg>
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="var(--orange-bg)">
                  <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0s.39-1.03 0-1.41L5.99 4.58zm12.37 12.37c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41l-1.06-1.06zm1.06-10.96c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06zM7.05 18.36c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06z" />
                </svg>
              </>
            )}
          </button>
          &nbsp;&nbsp;
          {/* Notes Button - theme toggle se pehle */}
          <button
            onClick={() => setShowNotesModal(true)}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--text)",
              padding: "6px 12px",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => (e.target.style.opacity = 0.8)}
            onMouseLeave={(e) => (e.target.style.opacity = 1)}
          >
            Scratchpad
          </button>
          {/* User Guide button — hidden
          &nbsp;&nbsp;
          <button
            onClick={() => setShowUserGuideModal(true)}
            title="User Guide"
            style={{
              background: "transparent",
              border: "none",
              color: "var(--text)",
              padding: "6px 12px",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "16px",
              display: "flex",
              alignItems: "center",
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => (e.target.style.opacity = 0.8)}
            onMouseLeave={(e) => (e.target.style.opacity = 1)}
          >
            <i className="bi bi-question-circle" style={{ fontSize: "18px" }}></i>
          </button>
          */}
          &nbsp;&nbsp;
          {currentRoute === "/chat-bot" && (
            <button
              style={{
                background: "transparent",
                border: "none",
                color: "var(--text)",
                padding: "6px 12px",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "opacity 0.2s",
              }}
              onClick={() => setShowConfirmationPopup(true)} // Show confirmation popup
            >
              Clear Chat
            </button>
          )}
          &nbsp;&nbsp;
          {user && (
            <div className="dropdown">
              <button
                style={{
                  background: "var(--teal-bg)",
                  border: "none",
                  color: "#fff",
                  padding: "5px 12px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  transition: "opacity 0.2s",
                }}
                className="dropdown-toggle"
                type="button"
                id="dropdownMenuButton"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {userName}
              </button>
              <ul
                className="dropdown-menu dropdown-menu-end"
                aria-labelledby="dropdownMenuButton"
              >
                <li>
                  <Link className="dropdown-item" to="/userprofile">
                    Profile
                  </Link>
                </li>
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() => setShowReminderModal(true)}
                    style={{
                      background: "transparent",
                      border: "none",
                      width: "100%",
                      textAlign: "left",
                    }}
                  >
                    Reminders Manager
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() => setShowScheduledTasksModal(true)}
                    style={{
                      background: "transparent",
                      border: "none",
                      width: "100%",
                      textAlign: "left",
                    }}
                  >
                    Scheduled Tasks
                  </button>
                </li>
                <li>
                  <Link
                    className="dropdown-item"
                    to="#"
                    onClick={handleMainSessionClick}
                  >
                    Main Topic
                  </Link>
                </li>
                {/* <li>
                  <Link className="dropdown-item" to="/userprofile">
                    Change Password
                  </Link>
                </li> */}
                <li>
                  <button className="dropdown-item" onClick={handleLogoutClick}>
                    Logout
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
      <ReplyModal
        show={showModal}
        onHide={() => setShowModal(false)}
        notification={selectedNotification}
        replyMessage={replyMessage}
        setReplyMessage={setReplyMessage}
        handleSendReply={() => handleSendReply(selectedNotification.id)} // Pass notification ID to handleSendReply
        disableSendButton={
          replyMessage.trim() === "" ||
          (selectedNotification?.user_query &&
            selectedNotification?.query_response)
        } // Disable send button if reply message is blank or user_query and query_response exist
        workflowUrl={workflowUrl} // Pass workflow URL to ReplyModal
      />
      <ConfirmationPopup
        show={showConfirmationPopup}
        handleClose={() => setShowConfirmationPopup(false)}
        title="Clear Chat"
        body="Are you sure you want to clear the chat?"
        handleConfirm={() => {
          onClearChat();
          setShowConfirmationPopup(false);
        }}
      />
      {/* Logout Confirmation Modal */}
      <ConfirmationPopup
        show={showLogoutModal}
        handleClose={handleLogoutCancel}
        handleConfirm={handleLogout}
        title="Do you really want to logout?"
        body="You will need to sign in again to access your account."
      />
      {/* Notes Modal */}
      <NotesModal
        show={showNotesModal}
        onHide={() => setShowNotesModal(false)}
        isDarkMode={isDarkMode}
        userId={userId} // Pass userId to NotesModal
      />
      {/* Reminder Modal */}
      <ReminderModal
        show={showReminderModal}
        onHide={() => setShowReminderModal(false)}
        isDarkMode={isDarkMode}
        userId={userId} // Pass userId to ReminderModal
      />
      {/* Scheduled Tasks Modal */}
      <ScheduledTasksModal
        show={showScheduledTasksModal}
        onHide={() => setShowScheduledTasksModal(false)}
        isDarkMode={isDarkMode}
        userId={userId}
      />
      {/* User Guide Modal */}
      <UserGuideModal
        show={showUserGuideModal}
        onHide={() => setShowUserGuideModal(false)}
      />
      {/* Theme Switch Loader */}
      <ThemeSwitchLoader
        isVisible={showThemeLoader}
        isDarkMode={pendingTheme !== null ? pendingTheme : isDarkMode}
      />
    </nav>
  );
}

export default Navbar;
